import { VERSION_LABELS, type CanonicalRankingPlacement, type VersionType } from '@primetime/shared';

export interface CatalogTrackVersion {
  trackVersionId: string;
  versionType: VersionType;
  durationSeconds?: number | null;
  availability?: string | null;
  revision?: number | null;
  downloadFilename?: string | null;
  releaseDate?: string | null;
  release_date?: string | null;
  previewVideoUrl?: string | null;
}

export interface CatalogTrackRow {
  isMix?: boolean;
  assetGroupId: string;
  trackId: string;
  rankingPlacements: CanonicalRankingPlacement[];
  releaseYear?: number | null;
  date: string;
  title: string;
  artist: string;
  artistDisplay?: string | null;
  genre?: string | null;
  subgenre?: string | null;
  style?: string | null;
  bpm?: number | null;
  key?: string | null;
  editorName?: string | null;
  editLabel?: string | null;
  versions: CatalogTrackVersion[];
  createdAt?: string;
  downloadCount?: number;
}

export interface CatalogDisplayRow extends CatalogTrackRow {
  rowKey: string;
  version: CatalogTrackVersion;
  displayVersions: CatalogTrackVersion[];
  cleanVersion?: CatalogTrackVersion;
  dirtyVersion?: CatalogTrackVersion;
  djEditVersion?: CatalogTrackVersion;
  displayVersionLabel: string;
}

const DEFAULT_CLEAN_DIRTY_ROW_KEY = 'default-clean-dirty';

function normalizedEditorName(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed || null;
}

export function getCatalogVersionDisplayLabel(version: CatalogTrackVersion, assetGroup?: Pick<CatalogTrackRow, 'editorName' | 'editLabel'>) {
  const editorName = normalizedEditorName(assetGroup?.editLabel) || normalizedEditorName(assetGroup?.editorName);
  const versionLabel = VERSION_LABELS[version.versionType];
  
  if (editorName && versionLabel) {
    if (editorName.includes(versionLabel)) return editorName;
    return `${editorName} ${versionLabel}`;
  }
  
  if (editorName) return editorName;
  if (versionLabel) return versionLabel;
  return '';
}

export function buildCatalogDisplayRows(tracks: CatalogTrackRow[]): CatalogDisplayRow[] {
  return tracks.flatMap((track) => {
    const rows: CatalogDisplayRow[] = [];
    const grouped = new Map<string, CatalogTrackVersion[]>();
    const others: CatalogTrackVersion[] = [];

    for (const version of track.versions) {
      if (['CLEAN', 'DIRTY', 'DJ_EDIT'].includes(version.versionType)) {
      const key = normalizedEditorName(track.editLabel) || normalizedEditorName(track.editorName) || DEFAULT_CLEAN_DIRTY_ROW_KEY;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(version);
      } else {
        others.push(version);
      }
    }

    for (const [editorKey, versions] of grouped.entries()) {
      const clean = versions.find(v => v.versionType === 'CLEAN');
      const dirty = versions.find(v => v.versionType === 'DIRTY');
      const djEdit = versions.find(v => v.versionType === 'DJ_EDIT');
      const primary = clean || dirty || djEdit;
      
      if (!primary) continue;

      let displayLabel = editorKey;
      if (editorKey === DEFAULT_CLEAN_DIRTY_ROW_KEY) {
        const parts = [];
        if (clean) parts.push('CLEAN');
        if (dirty) parts.push('DIRTY');
        if (djEdit) parts.push('DJ EDIT');
        displayLabel = parts.join(' / ');
      }

      rows.push({
        ...track,
        rowKey: `${track.trackId}:${editorKey}:grouped`,
        version: primary,
        displayVersions: versions,
        cleanVersion: clean,
        dirtyVersion: dirty,
        djEditVersion: djEdit,
        displayVersionLabel: displayLabel
      });
    }

    for (const version of others) {
      rows.push({
        ...track,
        rowKey: version.trackVersionId,
        version,
        displayVersions: [version],
        cleanVersion: version.versionType === 'CLEAN' ? version : undefined,
        dirtyVersion: version.versionType === 'DIRTY' ? version : undefined,
        djEditVersion: version.versionType === 'DJ_EDIT' ? version : undefined,
        displayVersionLabel: getCatalogVersionDisplayLabel(version, track)
      });
    }

    return rows;
  });
}

export function getPreferredVersion(row: CatalogDisplayRow, variant?: 'clean' | 'dirty' | 'djEdit') {
  if (variant === 'djEdit' && row.djEditVersion) return row.djEditVersion;
  if (variant === 'dirty' && row.dirtyVersion) return row.dirtyVersion;
  if (variant === 'clean' && row.cleanVersion) return row.cleanVersion;
  return row.version;
}

function isSafePublicPreviewUrl(value?: string | null) {
  if (!value) return false;
  return value.startsWith('/api/preview/') ||
    value.startsWith('/previews/video/') ||
    value.startsWith('previews/video/');
}

export function getCatalogVersionPreviewUrl(version?: CatalogTrackVersion | null) {
  const previewVideoUrl = version?.previewVideoUrl;
  return previewVideoUrl && isSafePublicPreviewUrl(previewVideoUrl) ? previewVideoUrl : null;
}

export function hasCleanDirtyPair(row: CatalogDisplayRow) {
  // Now represents if it is a grouped row of any core types
  return Boolean(row.cleanVersion || row.dirtyVersion || row.djEditVersion);
}
